import { useState } from "react";
import { Mail, MapPin, Clock, Send, Lock } from "lucide-react";
import { toast } from "sonner";
import { CONTACT_EMAIL, SERVICE_AREA } from "@/lib/content";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const submit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Website inquiry from ${form.name || "a visitor"}`);
    const body = encodeURIComponent(`${form.message}\n\nReply to: ${form.email}`);
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    toast.success("Opening your email app to send the message.");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="max-w-2xl">
        <p className="text-xs uppercase tracking-[0.2em] text-[#B87333] font-semibold">Contact</p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-[#2C2621] font-semibold">Let's talk</h1>
        <p className="mt-4 text-lg text-[#635B52]">Questions about a booking or a custom quote? We'd love to help.</p>
      </div>

      <div className="mt-12 grid lg:grid-cols-2 gap-12">
        <div className="space-y-4">
          {[
            { icon: Mail, t: "Email", d: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
            { icon: MapPin, t: "Service area", d: SERVICE_AREA },
            { icon: Clock, t: "Hours", d: "Mon–Fri 8am–6pm, Sat–Sun 9am–4pm (America/Chicago)" },
          ].map((c, i) => (
            <div key={i} className="bg-white rounded-2xl border border-[#E6DEC3] p-6 flex items-start gap-4 shadow-[0_4px_20px_rgba(44,38,33,0.04)]">
              <div className="w-11 h-11 rounded-full bg-[#B87333]/15 text-[#B87333] flex items-center justify-center shrink-0"><c.icon size={20} /></div>
              <div>
                <p className="font-semibold text-[#2C2621]">{c.t}</p>
                {c.href ? <a href={c.href} className="text-[#B87333] hover:underline" data-testid="contact-email-link">{c.d}</a> : <p className="text-[#635B52]">{c.d}</p>}
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E6DEC3] p-8 shadow-[0_4px_20px_rgba(44,38,33,0.04)] space-y-4" data-testid="contact-form">
          <div>
            <label className="text-sm font-medium text-[#2C2621]">Name</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full rounded-xl border border-[#E6DEC3] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#B87333]/30" data-testid="contact-name" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#2C2621]">Email</label>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 w-full rounded-xl border border-[#E6DEC3] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#B87333]/30" data-testid="contact-email" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#2C2621]">Message</label>
            <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="mt-1 w-full rounded-xl border border-[#E6DEC3] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#B87333]/30" data-testid="contact-message" />
          </div>
          <button type="submit" className="w-full bg-[#B87333] hover:bg-[#A06127] text-white font-medium rounded-full px-6 py-3 shadow-md transition-all inline-flex items-center justify-center gap-2" data-testid="contact-submit">
            <Send size={16} /> Send message
          </button>
        </form>
      </div>

      <div className="mt-16 max-w-3xl" data-testid="contact-faq">
        <p className="text-xs uppercase tracking-[0.2em] text-[#B87333] font-semibold">FAQ</p>
        <h2 className="mt-2 font-serif text-2xl sm:text-3xl text-[#2C2621] font-semibold">Common questions</h2>
        <div className="mt-6 rounded-2xl border border-[#5F8F87]/25 bg-[#F7F2E8] p-6" data-testid="faq-payment-secure">
          <p className="flex items-center gap-2 font-semibold text-[#1C1C1C]">
            <Lock size={16} className="shrink-0 text-[#5F8F87]" aria-hidden="true" />
            Is my online payment secure?
          </p>
          <p className="mt-2 pl-[26px] text-sm leading-relaxed text-[#1C1C1C]/75">
            Yes. Online payments are securely processed by <span className="text-[#B87333]">Stripe</span>. Masaya Hospitality Cleaning Services does not receive or store your full card number.
          </p>
        </div>
      </div>
    </div>
  );
}
